import { Component } from 'vue-property-decorator';
import Base from '../Base';
import { Button, Selector, StatusSwitch } from 'streamlabs-beaker';
import UserApi from '@/apis/user-api';
import { EPlatformNames } from '@/helpers/platforms';
import { cloneDeep } from 'lodash';
import { EModalName } from '@/helpers/modal';
import EditPlatformModal from './EditPlatformModal.vue';
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue';
import NotificationHelper from '@/helpers/notification';
import { EEditorType } from '@/helpers/editor';
import { Debounce } from 'lodash-decorators';

interface IStreamDestinationError {
  message: string;
  link?: string;
  link_text?: string;
}

export interface IStreamDestination {
  icon: string;
  name: string;
  type: TDestinationType;
  connected: boolean;
  is_destination?: boolean;
  username?: string;
  orientation?: string;
  allow_vertical?: boolean;
  error?: IStreamDestinationError;
}

export type TDestinationType = 'twitch' | 'youtube' | 'kick' | 'tiktok';

@Component({
  components: {
    SButton: Button,
    SSelector: Selector,
    SStatusSwitch: StatusSwitch,
    EditPlatformModal,
    ConfirmationModal,
  },
})
export default class PlatformSettings extends Base {
  selectedAccount: IStreamDestination = null;

  orientationOptions = [
    {
      value: 'horizontal',
      icon: 'icon-desktop',
      label: 'Horizontal',
    },
    {
      value: 'vertical',
      icon: 'icon-phone-case',
      label: 'Vertical',
    },
  ];

  get streamDestinations(): IStreamDestination[] {
    return [
      {
        icon: 'icon-twitch',
        name: 'Twitch',
        type: EPlatformNames.twitch,
        connected: this.hasTwitch,
        username: this.currentUser?.username,
        is_destination: true,
      },
      {
        icon: 'icon-youtube',
        name: 'YouTube',
        type: EPlatformNames.youtube,
        connected: false,
        is_destination: false,
      },
      {
        icon: 'icon-kick',
        name: 'Kick',
        type: EPlatformNames.kick,
        connected: false,
        is_destination: false,
      },
      {
        icon: 'icon-tiktok',
        name: 'Tiktok',
        type: EPlatformNames.tiktok,
        connected: false,
        is_destination: false,
      },
    ]
      .filter((destination: IStreamDestination) => {
        return this.availablePlatforms.includes(destination.type);
      })
      .map((destination: IStreamDestination) => {
        const connectedDestination = this.connectedAccounts.find(account => account.type === destination.type);
        destination.connected = !!connectedDestination;
        destination.is_destination = connectedDestination?.is_destination;
        destination.orientation = connectedDestination?.orientation;
        destination.allow_vertical = connectedDestination?.allow_vertical;
        destination.username = connectedDestination?.name || 'Not connected';
        destination.error = connectedDestination?.error;
        return destination;
      });
  }

  get connectedPlatformNames() {
    return this.connectedAccounts.reduce((acc, platform) => {
      acc[platform.type] = platform;
      return acc;
    }, []);
  }

  get editAccountModalName(): string {
    return EModalName.platform;
  }

  get disconnectPlatformConfirmationName(): string {
    return EModalName.disconnectPlatformConfirmation;
  }

  get confirmationModalWidth(): number {
    if (!this.isMobile) return 700;

    return 400;
  }

  get confirmationModalTitle(): string {
    return `Are you sure you want to disconnect ${this.selectedAccount?.name}?`;
  }

  onConnectPlatform(platform: string) {
    window.open(`/auth/${[platform]}?intent=link`, '_blank');
  }

  async toggleDestination(destination) {
    await UserApi.setStreamDestination(destination.type).then(response => {
      if (!response.success) return;

      const connectedAccounts = cloneDeep(this.connectedAccounts);
      const index = connectedAccounts.findIndex(account => account.type === destination.type);
      const account = connectedAccounts[index];
      account.is_destination = response.is_destination;
      connectedAccounts.splice(index, 1, account);

      this.userModule.setUserConnectedAccounts(connectedAccounts);
    });
  }

  @Debounce(500)
  async onOrientationChange(destinationType: TDestinationType, orientation: EEditorType.horizontal | EEditorType.vertical) {
    await UserApi.setPlatformOrientation(destinationType, orientation).then(response => {
      if (!response.success) return;

      const connectedAccounts = cloneDeep(this.connectedAccounts);
      const index = connectedAccounts.findIndex(account => account.type === destinationType);
      const account = connectedAccounts[index];
      account.orientation = response.orientation;
      connectedAccounts.splice(index, 1, account);

      this.userModule.setUserConnectedAccounts(connectedAccounts);
    });
  }

  async openAccountModal(account) {
    this.selectedAccount = account;
    this.$modal.show(this.editAccountModalName);
  }

  hideModal(modalName) {
    this.$modal.hide(modalName);
  }

  openDisconnectConfirmationModal() {
    this.$modal.show(this.disconnectPlatformConfirmationName);
  }

  async onDisconnectPlatform() {
    UserApi.unlinkAccount(this.selectedAccount.type).then(response => {
      if (!response.success) return;

      this.userModule.setUserConnectedAccounts(this.connectedAccounts.filter(account => account.type !== this.selectedAccount.type));
      this.hideModal(this.editAccountModalName);
      this.hideModal(this.disconnectPlatformConfirmationName);
    });
  }

  mounted() {
    window.addEventListener('message', this.onMessage);
  }

  beforeClose() {
    window.removeEventListener('message', this.onMessage);
  }

  async onMessage(message) {
    if (message.data && message.data.type === 'callback-success' && message.origin === window.origin) {
      this.loadUser(true);

      if (message.data.authIntent === 'reauth') {
        NotificationHelper.success('Account Reauthenticated successfully');
      }
    }

    if (message.data && message.data.type === 'error' && message.data.error?.message && message.origin === window.origin) {
      NotificationHelper.error(message.data.error?.message);
    }
  }

  accountReauthenicationNeeded(destination) {
    return destination?.error?.message === 'Reauthentication is required';
  }

  reauthenticate(destinationType: TDestinationType) {
    window.open(`/auth/${destinationType}?intent=reauth`, '_blank');
  }
}
